/**
 * <BuyerMatchedLeadsPanel buyerId={id} />
 *
 * Shows every active lead in this workspace that matches any of this
 * Cash Buyer's active buy-boxes. Distinct from the legacy `matched_deals`
 * on the buyer doc (which is based on the top_matches cache from the
 * legacy buyer-search engine) — this one queries the new Layer 3 endpoint.
 *
 * Endpoint:  GET /api/team/{member_id}/matched-leads
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, Target, ExternalLink, Sparkles } from "lucide-react";
import { api, fmtMoney } from "@/lib/api";

export default function BuyerMatchedLeadsPanel({ buyerId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!buyerId) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/team/${buyerId}/matched-leads`);
        if (alive) setData(data);
      } catch {
        if (alive) setData(null);
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [buyerId]);

  if (loading) {
    return (
      <div className="card mb-5 text-center py-6 text-sm text-[var(--ink-3)]" data-testid="buyer-matched-leads-loading">
        <Loader2 size={14} className="animate-spin inline mr-2" /> Loading matched leads…
      </div>
    );
  }
  if (!data) return null;

  const leads = data.leads || [];
  const activeBoxes = data.active_boxes || 0;

  return (
    <div className="card mb-5" data-testid="buyer-matched-leads-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow flex items-center gap-1.5">
            <Target size={11} className="text-indigo-600" /> Community · Layer 3
          </div>
          <div className="font-display font-bold text-lg mt-0.5">Matched leads</div>
          <div className="text-xs text-[var(--ink-3)]">
            Active leads in your pipeline that fit this buyer&apos;s buy-boxes.
          </div>
        </div>
        {leads.length > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300">
            <Sparkles size={10} /> {leads.length}
          </span>
        )}
      </div>

      {activeBoxes === 0 ? (
        <div className="text-center py-6 text-sm text-[var(--ink-3)]" data-testid="buyer-matched-leads-no-boxes">
          <Target size={26} className="mx-auto opacity-40 mb-2" />
          <p>No active buy-boxes yet. Define one above to start matching.</p>
        </div>
      ) : leads.length === 0 ? (
        <div className="text-center py-6 text-sm text-[var(--ink-3)]" data-testid="buyer-matched-leads-empty">
          <MapPin size={26} className="mx-auto opacity-40 mb-2" />
          <p>No leads in your pipeline currently fit any of the <strong>{activeBoxes}</strong> active buy-box{activeBoxes === 1 ? "" : "es"}.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="buyer-matched-leads-list">
          {leads.map((lead) => (
            <Link
              key={lead.id}
              to={`/app/leads/${lead.id}/report`}
              className="flex items-center gap-3 p-3 rounded-lg border border-[var(--line)] hover:border-indigo-300 hover:bg-indigo-50/40 transition-colors"
              data-testid={`buyer-matched-lead-${lead.id}`}
            >
              <MapPin size={14} className="text-[var(--ink-3)] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{lead.property_address}</div>
                <div className="text-xs text-[var(--ink-3)] truncate">
                  {[lead.city, lead.state].filter(Boolean).join(", ")}
                  {lead.estimated_arv ? ` · ARV ${fmtMoney(lead.estimated_arv)}` : ""}
                  {lead.asking_price ? ` · Asking ${fmtMoney(lead.asking_price)}` : ""}
                </div>
                {Array.isArray(lead._matched_boxes) && lead._matched_boxes.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {lead._matched_boxes.slice(0, 3).map((name) => (
                      <span
                        key={name}
                        className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700"
                      >
                        <Target size={8} /> {name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {lead.ai_score != null && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                  AI {lead.ai_score}
                </span>
              )}
              <ExternalLink size={12} className="text-[var(--ink-3)] shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
